import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n, type TranslationKey } from '../i18n';

interface SupportLog {
  id: number;
  adminEmail: string;
  orgName: string;
  action: string;
  reason: string;
  ip_address: string | null;
  started_at: string;
  ended_at: string | null;
}

// Action de support (valeur technique de l'API) → libellé traduit
const ACTION_KEYS: Record<string, TranslationKey> = {
  view_dashboard: 'pg_support_action_view_dashboard',
  view_users: 'pg_support_action_view_users',
  impersonate: 'pg_support_action_impersonate',
  control_relay: 'pg_support_action_control_relay',
};

export default function AdminSupportLogsPage() {
  const { token } = useAuth();
  const { t, locale } = useI18n();
  const [logs, setLogs] = useState<SupportLog[]>([]);
  const [loading, setLoading] = useState(true);

  const API = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

  useEffect(() => {
    fetch(`${API}/api/admin/support-logs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { if (data.success) setLogs(data.logs); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" /></div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('adm_logs_title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('adm_logs_subtitle')}</p>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <p className="text-gray-500">{t('adm_logs_empty')}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('adm_logs_th_admin')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('adm_logs_th_client')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('adm_logs_th_action')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('adm_logs_th_reason')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('adm_logs_th_start')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('adm_logs_th_end')}</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">{t('adm_logs_th_status')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const isActive = !log.ended_at;
                const duration = log.ended_at
                  ? Math.round((new Date(log.ended_at).getTime() - new Date(log.started_at).getTime()) / 60000)
                  : null;

                return (
                  <tr key={log.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{log.adminEmail}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{log.orgName}</td>
                    <td className="px-4 py-3 text-gray-600">{ACTION_KEYS[log.action] ? t(ACTION_KEYS[log.action]) : log.action.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={log.reason}>{log.reason}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {new Date(log.started_at).toLocaleString(locale)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {log.ended_at ? `${duration} min` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        isActive ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isActive ? `🔴 ${t('adm_logs_active')}` : t('adm_logs_ended')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
